import { useMemo } from "react";

// Third party
import clsx from "clsx";
// MUI
import { Stack, SvgIcon, Button, Box } from "@mui/material";

// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
import SimpleIconButton from "@/Layouts/components/Buttons/SimpleIconButton";
import ConfiguredAccountsList from "./ConfiguredAccountsList";

// Helpers
import { or } from "@/Common/helpers/sonar";

// Types
import { TGetProviderList } from "@/Settings/api/AdministrationTools/queries";
import { IIntegrationProvider } from "@/Settings/types/administrationTools/model";
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";

// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";

interface IProviderAuthenticatorProps {
	providerOptions: TGetProviderList[];
	payrollProvider: IIntegrationProvider;
	selectedProvider: TGetProviderList | undefined;
	isLoading: boolean;
	shouldDisableDeleteProvider: boolean;
	isProviderLimitReached: boolean;
	index: number;
	isLastItem: boolean;
	timezone: string;
	disabledOptions: string[];
	onAdd: () => void;
	onDelete: (index: number) => void;
	onOpenAuthenticateDialog: () => void;
	onUpdate: (index: number, updatedProvider: TGetProviderList) => void;
	onDeleteAwaitingAccount: (providerName: string, displayId: string) => void;
}

const ProviderAuthenticator = (props: IProviderAuthenticatorProps) => {
	const {
		providerOptions,
		selectedProvider,
		payrollProvider,
		isLoading,
		shouldDisableDeleteProvider,
		isProviderLimitReached,
		index,
		isLastItem,
		disabledOptions,
		timezone,
		onAdd,
		onDelete,
		onOpenAuthenticateDialog,
		onUpdate,
		onDeleteAwaitingAccount,
	} = props;

	/***************************************** HANDLERS ****************************************/

	const handleSelectProvider = (newSelection: TSelectValue) => {
		onUpdate(index, newSelection as TGetProviderList);
	};

	/***************************************** MEMOS ****************************************/

	const shouldDisableChangeProvider = useMemo(() => {
		return !!payrollProvider?.configuredAccounts?.length;
	}, [payrollProvider]);

	/***************************************** RENDERING ****************************************/

	return (
		<Stack display={"flex"} direction={"column"} spacing={0.5} alignItems={"flex-start"} justifyContent={"center"}>
			<Stack display={"flex"} direction={"row"} alignItems={"center"} height={"40px"}>
				<Box className="w-[216px]">
					<SmartSelector
						selectBy={"provider"}
						showBy={"providerName"}
						loading={isLoading}
						options={providerOptions}
						selected={selectedProvider}
						disabled={isLoading || shouldDisableChangeProvider}
						disabledOptions={disabledOptions}
						onChangeAction={handleSelectProvider}
						inputProps={{
							placeholder: "Select a provider",
							sx: { width: "216px" },
						}}
						listBoxProps={{
							sx: { maxHeight: "220px" },
						}}
					/>
				</Box>
				<Button
					onClick={onOpenAuthenticateDialog}
					variant="contained"
					disabled={!selectedProvider}
					disableElevation
					classes={{
						root: clsx("!rounded-md !w-[86px] !h-[30px] !ml-3 !text-sm !text-white !capitalize !font-sans !font-normal", {
							"!bg-primary hover:!shadow-primary-shadow-50": selectedProvider,
							"!bg-disabled": !selectedProvider,
						}),
					}}
				>
					Authenticate
				</Button>
				<SimpleDoubleChecking
					disabled={or(shouldDisableDeleteProvider, !!selectedProvider)}
					className="!text-gray-blue"
					color={"var(--gray-blue-color)"}
					confirmationButtonColor={"var(--gray-blue-color)"}
					icon={
						<CloseRoundedIcon
							fontSize="small"
							className={clsx({
								"!text-default": !selectedProvider,
								"!text-disabled": selectedProvider,
							})}
						/>
					}
					iconHover={<CloseRoundedIcon fontSize="small" />}
					confirmationIcon={<SvgIcon className="!ml-1 !mt-0.5" component={TrashIcon} fontSize="small" />}
					onDeleteAction={() => onDelete(index)}
				/>
				{!isLastItem ? null : (
					<SimpleIconButton
						className={clsx("!w-10 !h-10", {
							"!text-primary": selectedProvider,
							"!text-disabled": !selectedProvider,
						})}
						icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
						iconHover={<AddCircleRounded fontSize="medium" />}
						disabled={!selectedProvider || isProviderLimitReached}
						onClick={onAdd}
					/>
				)}
			</Stack>
			<ConfiguredAccountsList
				providerName={payrollProvider.providerName}
				configuredAccounts={payrollProvider?.configuredAccounts ?? []}
				timezone={timezone}
				index={index}
				onDeleteAwaitingAccount={onDeleteAwaitingAccount}
			/>
		</Stack>
	);
};

export default ProviderAuthenticator;
